import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useBalances } from '../../openings/api/useOpenings';
import { Loading } from '../../../shared/ui/Loading';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { useDashboardSummary } from '../../reports/api/useReports';
import { currencyClass } from '../../../shared/ui/currency-color';

type Period = 'today' | '7d' | '30d' | 'month';

function fmt2(s: string) {
  return parseFloat(s).toFixed(2);
}

function fmtShort(s: string) {
  const n = parseFloat(s);
  if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(1) + 'k';
  return n.toFixed(2);
}

export function DashboardShell() {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>('today');
  const balances = useBalances();
  const summary = useDashboardSummary();

  const periods: { key: Period; label: string }[] = [
    { key: 'today', label: t('dashboard.period_today') },
    { key: '7d', label: t('dashboard.period_7d') },
    { key: '30d', label: t('dashboard.period_30d') },
    { key: 'month', label: t('dashboard.period_month') },
  ];

  const netMru = summary.data ? parseFloat(summary.data.todayNetMru) : 0;
  const isPositive = netMru >= 0;

  return (
    <>
      {/* Period tabs */}
      <div className="period-tabs" role="tablist" aria-label={t('dashboard.summary_label')}>
        {periods.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={period === p.key}
            className={`period-tabs__item${period === p.key ? ' is-active' : ''}`}
            onClick={() => setPeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {summary.isLoading ? <Loading /> : null}
      {summary.error ? <ErrorMessage error={summary.error} /> : null}

      {summary.data ? (
        <>
          {/* Hero card — net volume */}
          <div className="hero-card" aria-label={t('dashboard.summary_label')}>
            <p className="hero-card__label">{t('dashboard.volume_mru')}</p>
            <p className="hero-card__amount">
              {fmtShort(summary.data.todayNetMru)}{' '}
              <span style={{ fontSize: '0.6em', fontWeight: 400 }}>MRU</span>
            </p>
            <p className="hero-card__sub">
              {isPositive ? '+' : ''}
              {fmt2(summary.data.todayNetMru)} MRU
            </p>
          </div>

          {/* 2×2 satellite grid */}
          <div className="satellite-grid">
            <div className="satellite-card">
              <p className="satellite-card__label">{t('dashboard.purchases_label')}</p>
              <p className="satellite-card__amount satellite-card__amount--out">
                {fmtShort(summary.data.todayPurchases.totalMru)}
              </p>
              <p className="satellite-card__sub">
                {t('dashboard.operations_count', {
                  count: summary.data.todayPurchases.count,
                })}
              </p>
            </div>
            <div className="satellite-card">
              <p className="satellite-card__label">{t('dashboard.sales_label')}</p>
              <p className="satellite-card__amount satellite-card__amount--in">
                {fmtShort(summary.data.todaySales.totalMru)}
              </p>
              <p className="satellite-card__sub">
                {t('dashboard.operations_count', {
                  count: summary.data.todaySales.count,
                })}
              </p>
            </div>
            <div className="satellite-card">
              <p className="satellite-card__label">{t('dashboard.receivables_label')}</p>
              <p className="satellite-card__amount">
                {fmtShort(summary.data.openReceivables.totalMru)}
              </p>
              <p className="satellite-card__sub">
                {t('dashboard.operations_count', {
                  count: summary.data.openReceivables.count,
                })}
              </p>
            </div>
            <div className="satellite-card">
              <p className="satellite-card__label">{t('dashboard.payables_label')}</p>
              <p className="satellite-card__amount">
                {fmtShort(summary.data.openPayables.totalMru)}
              </p>
              <p className="satellite-card__sub">
                {t('dashboard.operations_count', {
                  count: summary.data.openPayables.count,
                })}
              </p>
            </div>
          </div>

          {summary.data.lowBalanceCurrencies.length > 0 ? (
            <div className="banner banner--warn" role="status">
              {t('dashboard.low_balance_warning', {
                count: summary.data.lowBalanceCurrencies.length,
              })}
              <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                {summary.data.lowBalanceCurrencies.map((c) => (
                  <span key={c.code} className="badge badge--warn">
                    {c.code}
                  </span>
                ))}
              </span>
            </div>
          ) : null}
        </>
      ) : null}

      {/* Currency mini-rows */}
      {balances.isLoading ? <Loading /> : null}
      {balances.error ? <ErrorMessage error={balances.error} /> : null}
      {balances.data && balances.data.length > 0 ? (
        <>
          <p className="section-label" style={{ marginBlockStart: 'var(--sp-4)' }}>
            {t('dashboard.balances_label')}
          </p>
          <ul className="currency-row-list" aria-label={t('balances.title')}>
            {balances.data.map((row) => {
              const isLow =
                row.lowBalanceThreshold !== null &&
                parseFloat(row.cachedAmount) <= parseFloat(row.lowBalanceThreshold);
              return (
                <li key={row.currencyId}>
                  <Link to={`/currencies/${row.currencyId}/history`} className="currency-row">
                    <span
                      className={`currency-row__badge ${currencyClass(row.code)}`}
                      aria-hidden="true"
                    >
                      {row.code}
                    </span>
                    <span className="currency-row__code">{row.name}</span>
                    {isLow ? <span className="badge badge--warn">{t('balances.low')}</span> : null}
                    <span className="currency-row__amount">
                      {parseFloat(row.cachedAmount).toFixed(row.decimalPlaces)} {row.code}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </>
  );
}
